'use client';

import { useState, useEffect } from 'react';
import {
	Crown,
	Zap,
	TrendingUp,
	BarChart3,
	Sparkles,
	Edit,
	HelpCircle,
	ExternalLink
} from 'lucide-react';
import { QuotaUpgradeModal } from './quota-upgrade-modal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

function cn(...inputs: (string | undefined | null | false)[]): string {
	return inputs.filter(Boolean).join(' ');
}

const buttonVariants = cva(
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground hover:bg-primary/90',
				destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				outline:
					'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
				secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline'
			},
			size: {
				default: 'h-10 px-4 py-2',
				sm: 'h-9 rounded-md px-3',
				lg: 'h-11 rounded-md px-8',
				icon: 'h-10 w-10'
			}
		},
		defaultVariants: {
			variant: 'default',
			size: 'default'
		}
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	}
);
Button.displayName = 'Button';

interface LeadQuotaData {
	allowed: boolean;
	remaining: number;
	total: number;
	used: number;
	subscription?: any;
}

interface LeadQuotaBarProps {
	siteId: string | null;
	className?: string;
	onUpgradeClick?: () => void;
	refreshTrigger?: number; // Can be used to trigger a refresh from parent
	onRefreshData?: (refreshFn: () => Promise<void>) => void; // Callback to expose refresh function
}

export function LeadQuotaBar({
	siteId,
	className,
	onUpgradeClick,
	refreshTrigger,
	onRefreshData
}: LeadQuotaBarProps) {
	const [quotaData, setQuotaData] = useState<LeadQuotaData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
	const [previousUsed, setPreviousUsed] = useState<number>(0);

	const fetchQuotaData = async () => {
		if (!siteId) return;

		try {
			setLoading(true);
			const response = await fetch(`/api/sites/${siteId}/lead-quota`);

			if (!response.ok) {
				throw new Error('Failed to fetch quota data');
			}

			const data = await response.json();

			// Store previous used value for animation
			if (quotaData) {
				setPreviousUsed(quotaData.used);
			}

			setQuotaData(data);
		} catch (err) {
			console.error('Error fetching quota data:', err);
			setError(err instanceof Error ? err.message : 'Failed to load quota');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchQuotaData();
	}, [siteId, refreshTrigger]);

	// Expose refresh function to parent component
	useEffect(() => {
		if (onRefreshData) {
			onRefreshData(fetchQuotaData);
		}
	}, [onRefreshData]);

	const handlePlanChanged = () => {
		// Refresh data after plan change
		fetchQuotaData();
		setUpgradeModalOpen(false);
	};

	if (loading) {
		return (
			<div
				className={cn(
					'bg-white/90 backdrop-blur-lg rounded-lg p-4 border border-gray-200',
					className
				)}
			>
				<div className="animate-pulse">
					<div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
					<div className="h-2 bg-gray-200 rounded w-full mb-4"></div>
					<div className="h-8 bg-gray-200 rounded w-1/2"></div>
				</div>
			</div>
		);
	}

	if (error || !quotaData) {
		return (
			<div
				className={cn(
					'bg-white/90 backdrop-blur-lg rounded-lg p-4 border border-gray-200',
					className
				)}
			>
				<div className="text-gray-600 text-sm">Unable to load quota information</div>
			</div>
		);
	}

	const { remaining, total, used, subscription } = quotaData;
	const percentage = total > 0 ? (used / total) * 100 : 0;
	const previousPercentage = total > 0 ? (previousUsed / total) * 100 : 0;
	const hasIncreased = used > previousUsed;
	const isNearLimit = percentage >= 80;
	const isOverLimit = percentage >= 100;

	const planName = subscription?.plan?.name || 'Free';
	const isFreePlan = planName === 'Free' || !subscription;

	return (
		<>
			<div
				className={cn(
					'bg-white/90 backdrop-blur-lg rounded-lg p-4 space-y-3 border border-gray-200',
					className
				)}
			>
				{/* Header */}
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-2">
						<div className="p-1.5 bg-emerald-100 rounded-lg">
							<BarChart3 className="h-4 w-4 text-emerald-600" />
						</div>
						<div className="flex items-center space-x-1">
							<h3 className="text-gray-900 font-semibold">Lead & Send Quota</h3>
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>
										<button className="text-gray-500 hover:text-gray-700 transition-colors">
											<HelpCircle className="h-3 w-3" />
										</button>
									</TooltipTrigger>
									<TooltipContent className="max-w-xs">
										<p>
											All leads come with premium send through NOS.email
											network
										</p>
										<a
											href="https://nos.email"
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center space-x-1 text-emerald-400 hover:text-emerald-300 text-sm mt-1"
										>
											<span>Visit NOS.email</span>
											<ExternalLink className="h-3 w-3" />
										</a>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</div>
						{!isFreePlan && (
							<div className="flex items-center space-x-1 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium">
								<Crown className="h-3 w-3" />
								<span>{planName}</span>
							</div>
						)}
					</div>
					<div className="flex items-center space-x-2">
						<div className="text-gray-900 text-sm font-medium">
							{used.toLocaleString()}/{total.toLocaleString()}
						</div>
						<Button
							variant="ghost"
							size="icon"
							className="text-gray-600 hover:text-gray-900 h-8 w-8"
							onClick={() => setUpgradeModalOpen(true)}
						>
							<Edit className="h-3 w-3" />
							<span className="sr-only">Edit Lead Quota</span>
						</Button>
					</div>
				</div>

				{/* Progress Bar */}
				<div className="space-y-2">
					<div className="w-full bg-gray-200 border border-gray-300 rounded-full h-6 overflow-hidden">
						<div
							className={`h-full bg-emerald-500 transition-all duration-1000 ease-out ${
								hasIncreased ? 'shadow-[0_0_20px_rgba(34,197,94,0.6)]' : ''
							}`}
							style={{ width: `${Math.min(percentage, 100)}%` }}
						/>
					</div>

					{/* Status Text */}
					<div className="flex items-center justify-between text-sm">
						<span className="font-medium text-gray-900">
							{remaining.toLocaleString()} remaining
						</span>
						<div className="flex items-center space-x-2">
							<span className="text-gray-700">{percentage.toFixed(1)}% used</span>
							{percentage >= 100 && (
								<button
									onClick={() => setUpgradeModalOpen(true)}
									className="text-emerald-600 text-xs font-black underline hover:text-emerald-700 transition-colors duration-200"
								>
									Upgrade
								</button>
							)}
						</div>
					</div>
				</div>
			</div>

			<QuotaUpgradeModal
				isOpen={upgradeModalOpen}
				onClose={() => setUpgradeModalOpen(false)}
				siteId={siteId || ''}
				quotaType="lead"
				currentSubscription={quotaData?.subscription}
				onPlanChanged={handlePlanChanged}
			/>
		</>
	);
}
